import React from "react";
import { Box, Card, Grid, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import CasinoIcon from "@mui/icons-material/Casino";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import Ball from "./Ball";
import CountdownBox from "./CountdownBox";
import LiveDot from "./LiveDot";
import { elevatedCard, elevatedRow } from "./cardStyle";

const LotteryHero = ({
  lottery,
  isAuthenticated,
  hasMultiple,
  countdown,
  lastResult,
  maxNumber,
  prizeLabel,
  ticketPriceLabel,
}) => {
  const theme = useTheme();
  const lastResultPrizeLabel =
    lastResult?.prizeDisplay || (lastResult?.prizeSc != null ? `${lastResult.prizeSc} SC` : null);

  return (
    <Card
      sx={{
        background:
          "linear-gradient(135deg, #0d2b16 0%, #123c1f 40%, #191919 100%)",
        border: "1px solid #03B34955",
        borderRadius: "18px",
        p: { xs: 2.5, md: 4 },
        mt: isAuthenticated || hasMultiple ? 0 : "60px",
        position: "relative",
        overflow: "hidden",
        ...elevatedCard,
      }}
    >
      <Box sx={{ position: "absolute", right: -30, top: -30, opacity: 0.07 }}>
        <CasinoIcon sx={{ fontSize: 260, color: "#fff" }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: "10%",
          top: "20%",
          width: 220,
          height: 220,
          borderRadius: "50%",
          background: "radial-gradient(circle, #03B34933 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={7}>
          <Typography
            sx={{
              color: theme.palette.custom.green01,
              fontWeight: 800,
              letterSpacing: 1.5,
              fontSize: { xs: "20px", md: "26px" },
              textTransform: "uppercase",
            }}
          >
            <ConfirmationNumberIcon sx={{ fontSize: { xs: 22, md: 26 }, mr: 0.7, verticalAlign: "-5px" }} />
            {lottery?.name || "Lottery"}
          </Typography>
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 900,
              fontSize: { xs: "32px", md: "48px" },
              lineHeight: 1.15,
              mt: 0.8,
              textShadow: "0 0 30px rgba(3,179,73,0.35)",
            }}
          >
            Win{" "}
            <Box component="span" sx={{ color: theme.palette.custom.green01 }}>
              {prizeLabel || "--"}
            </Box>
          </Typography>
          <Typography sx={{ color: "#ffffffbb", mt: 0.8, fontSize: { xs: "13px", md: "15px" } }}>
            Pick {lottery?.ballCount || 3} numbers. Match all {lottery?.ballCount || 3} in the draw and the prize is yours.
          </Typography>

          <Box
            sx={{
              mt: 2,
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              background: "#00000044",
              border: `1px solid ${theme.palette.custom.green01}44`,
              borderRadius: "20px",
              px: 1.8,
              py: 0.8,
              ...elevatedRow,
            }}
          >
            <AllInclusiveIcon sx={{ fontSize: 18, color: theme.palette.custom.green01 }} />
            <Typography sx={{ color: "#ffffffdd", fontSize: "12px", fontWeight: 600 }}>
              {ticketPriceLabel
                ? `Ticket: ${ticketPriceLabel} — jump in anytime before the draw!`
                : "No limit — jump in anytime before the draw!"}
            </Typography>
          </Box>

          {lastResult && (
            <Box
              sx={{
                mt: 2,
                display: "inline-flex",
                alignItems: "center",
                gap: 1.2,
                background: "#00000044",
                border: `1px solid ${theme.palette.custom.yellow}44`,
                borderRadius: "12px",
                px: 1.8,
                py: 1.1,
                ...elevatedRow,
              }}
            >
              <EmojiEventsIcon sx={{ fontSize: 20, color: theme.palette.custom.yellow }} />
              <Typography sx={{ color: "#ffffffdd", fontSize: "12px" }}>
                Last draw paid out{" "}
                <Box component="span" sx={{ color: theme.palette.custom.yellow, fontWeight: 800, fontSize: "14px" }}>
                  {lastResultPrizeLabel}
                </Box>
                {" — could you be next?"}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {lastResult?.drawnNumbers?.map((n) => (
                  <Ball key={n} num={n} max={maxNumber} size={22} />
                ))}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={5}>
          <Typography
            sx={{
              color: countdown?.closing ? theme.palette.custom.red : "#ffffffaa",
              fontSize: "12px",
              textTransform: "uppercase",
              letterSpacing: 1,
              mb: 1,
              textAlign: { xs: "left", md: "center" },
              fontWeight: countdown?.closing ? 800 : 400,
            }}
          >
            {(countdown?.ended || countdown?.closing) && <LiveDot color={countdown?.ended ? "#00ff41" : "#fa114f"} />}
            {countdown?.ended ? "Drawing now" : countdown?.closing ? "Closing soon!" : "Draw closes in"}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1, md: 1.8 },
              justifyContent: { xs: "flex-start", md: "center" },
            }}
          >
            <CountdownBox value={countdown?.days} label="Days" />
            <CountdownBox value={countdown?.hours} label="Hrs" />
            <CountdownBox value={countdown?.minutes} label="Min" />
            <CountdownBox value={countdown?.seconds} label="Sec" />
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

export default LotteryHero;
